import React, { useState } from 'react';
import { createWidget } from '../../services/api';
import SearchableTicker from '../SearchableTicker';
import ExpirationDateSelect from '../Watchlist/ExpirationDateSelect';
import StrikePriceSelect from '../Watchlist/StrikePriceSelect';
import { useLastClickedTicker } from '../../contexts/LastClickedTickerContext';
import { Lock, Unlock, Link2, TrendingUp } from 'lucide-react';

/**
 * AddWidgetModal - 3-Step Wizard for Creating Widgets
 *
 * Step 1: Widget Type Selection
 * Step 2: Static vs Dynamic Behavior
 * Step 3: Configuration (ticker, time, size)
 */
function AddWidgetModal({ dashboard, onClose, onWidgetAdded }) {
  const [step, setStep] = useState(1);
  const [widgetType, setWidgetType] = useState(null);
  const [isStatic, setIsStatic] = useState(null);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [dateRange, setDateRange] = useState('1y');
  const [size, setSize] = useState('medium');
  const [loading, setLoading] = useState(false);

  // Options chart specific state
  const [optionType, setOptionType] = useState('call');
  const [expiration, setExpiration] = useState('');
  const [strike, setStrike] = useState('');

  const { lastClickedTicker, lastClickedContract } = useLastClickedTicker();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTicker || !selectedTicker.value) {
      alert('Please select a ticker symbol');
      return;
    }

    // Additional validation for options chart
    if (widgetType === 'options_chart') {
      if (!expiration) {
        alert('Please select an expiration date');
        return;
      }
      if (!strike) {
        alert('Please select a strike price');
        return;
      }
    }

    setLoading(true);
    try {
      let widgetData;

      if (widgetType === 'options_chart') {
        widgetData = {
          ticker: selectedTicker.value.toUpperCase(),
          widget_type: widgetType,
          size: size,
          use_dynamic_ticker: !isStatic,
          config: {
            option_type: optionType,
            strike: parseFloat(strike),
            expiration: expiration,
            date_range: dateRange,
          },
        };
      } else {
        widgetData = {
          ticker: selectedTicker.value.toUpperCase(),
          widget_type: widgetType,
          size: size,
          use_dynamic_ticker: !isStatic,
          config: {
            date_range: dateRange,
            indicators: [], // Indicators added after widget creation
          },
        };
      }

      await createWidget(dashboard.id, widgetData);
      onWidgetAdded();
      onClose();
    } catch (error) {
      console.error('Error creating widget:', error);
      alert('Failed to create widget');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      onClose();
    } else {
      setStep(step - 1);
    }
  };

  const handleNext = () => {
    if (step === 1 && !widgetType) {
      alert('Please select a widget type');
      return;
    }
    if (step === 2 && isStatic === null) {
      alert('Please choose static or dynamic behavior');
      return;
    }
    setStep(step + 1);
  };

  // Step 1: Widget Type Selection
  const renderStepOne = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-400 mb-4">
        Choose the type of widget you want to add to your dashboard
      </p>

      <div className="grid grid-cols-1 gap-4">
        {/* Price Chart Widget */}
        <button
          onClick={() => {
            setWidgetType('price_chart');
            setStep(2);
          }}
          className={`p-6 border-2 rounded-lg text-left transition-all hover:border-primary ${
            widgetType === 'price_chart' ? 'border-primary bg-primary/10' : 'border-muted'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center text-2xl">
              📈
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text mb-2">Price Chart</h3>
              <p className="text-sm text-gray-400">
                Interactive price chart with technical indicators. Add moving averages, MACD, RSI, and more after creation.
              </p>
            </div>
          </div>
        </button>

        {/* Options Chart Widget */}
        <button
          onClick={() => {
            setWidgetType('options_chart');
            setDateRange('3m'); // Default to 3m for options
            setStep(2);
          }}
          className={`p-6 border-2 rounded-lg text-left transition-all hover:border-primary ${
            widgetType === 'options_chart' ? 'border-primary bg-primary/10' : 'border-muted'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text mb-2">Options Premium Chart</h3>
              <p className="text-sm text-gray-400">
                Historical premium price chart for a specific options contract. Track daily closing prices over time.
              </p>
            </div>
          </div>
        </button>

        {/* Federal Reserve Data Widget (Coming Soon) */}
        <div className="p-6 border-2 border-muted rounded-lg text-left opacity-50 cursor-not-allowed">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center text-2xl">
              🏦
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text mb-2">
                Federal Reserve Data
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-700 text-gray-400 rounded">Coming Soon</span>
              </h3>
              <p className="text-sm text-gray-400">
                Economic indicators from the Federal Reserve including interest rates, inflation, and employment data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: Static vs Dynamic Behavior
  const renderStepTwo = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-400 mb-4">
        How should this widget behave when you click a ticker in Screener or Watchlist?
      </p>

      <div className="grid grid-cols-1 gap-4">
        {/* Dynamic Widget */}
        <button
          onClick={() => {
            setIsStatic(false);
            // Default to last clicked ticker for dynamic widgets
            if (!selectedTicker && lastClickedTicker) {
              setSelectedTicker({ value: lastClickedTicker, label: lastClickedTicker });
            }
            setStep(3);
          }}
          className={`p-6 border-2 rounded-lg text-left transition-all hover:border-primary ${
            isStatic === false ? 'border-primary' : 'border-muted'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-600/20 rounded flex items-center justify-center">
              <Unlock className="w-8 h-8 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text mb-2">Dynamic Widget</h3>
              <p className="text-sm text-gray-400 mb-3">
                This widget will update to show whatever ticker you click in Screener or Watchlist tables.
              </p>
              <div className="bg-gray-800/50 border border-gray-700 rounded p-3">
                <p className="text-xs text-gray-400 mb-1">Example:</p>
                <p className="text-xs text-gray-300">
                  Click "AAPL" in Screener → This widget shows AAPL<br />
                  Click "TSLA" in Watchlist → This widget shows TSLA
                </p>
              </div>
            </div>
          </div>
        </button>

        {/* Static Widget */}
        <button
          onClick={() => {
            setIsStatic(true);
            setStep(3);
          }}
          className={`p-6 border-2 rounded-lg text-left transition-all hover:border-primary ${
            isStatic === true ? 'border-primary' : 'border-muted'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gray-700/50 rounded flex items-center justify-center">
              <Lock className="w-8 h-8 text-gray-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-text mb-2">Static Widget</h3>
              <p className="text-sm text-gray-400 mb-3">
                This widget will always show the same ticker, regardless of what you click elsewhere.
              </p>
              <div className="bg-gray-800/50 border border-gray-700 rounded p-3">
                <p className="text-xs text-gray-400 mb-1">Example:</p>
                <p className="text-xs text-gray-300">
                  Always show SPY to track the market<br />
                  Always show VIX to monitor volatility
                </p>
              </div>
            </div>
          </div>
        </button>
      </div>

      <button
        onClick={handleBack}
        className="w-full px-4 py-2 bg-muted text-text rounded hover:bg-muted/80 transition"
      >
        ← Back
      </button>
    </div>
  );

  // Cascading dependency handlers for options form
  const handleTickerChange = (ticker) => {
    setSelectedTicker(ticker);
    setExpiration('');  // Clear downstream
    setStrike('');      // Clear downstream
  };

  const handleOptionTypeChange = (e) => {
    setOptionType(e.target.value);
    setStrike('');  // Clear strike only (expiration stays valid)
  };

  const handleExpirationChange = (exp) => {
    setExpiration(exp);
    setStrike('');  // Clear strike only
  };

  // Step 3: Configuration for Price Chart
  const renderStepThreePriceChart = () => (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Ticker Symbol
          </label>
          <SearchableTicker
            onChange={setSelectedTicker}
            value={selectedTicker}
            placeholder="Search ticker (e.g., AAPL, SPY, VIX)..."
          />
          {!isStatic && (
            <p className="text-xs text-gray-400 mt-2">
              Current default: <span className="text-primary font-medium">{lastClickedTicker}</span>
              {' '}(This will update when you click tickers in Screener/Watchlist)
            </p>
          )}
          {isStatic && (
            <p className="text-xs text-gray-400 mt-2">
              This widget will always display {selectedTicker?.value || 'the selected ticker'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Date Range
          </label>
          <div className="grid grid-cols-3 gap-2">
            {['1m', '3m', '6m', '1y', '2y', '5y'].map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setDateRange(range)}
                className={`px-3 py-2 rounded text-sm transition ${
                  dateRange === range
                    ? 'bg-primary text-white'
                    : 'bg-muted text-text hover:bg-muted/80'
                }`}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Widget Size
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
            ].map((sizeOption) => (
              <button
                key={sizeOption.value}
                type="button"
                onClick={() => setSize(sizeOption.value)}
                className={`px-3 py-2 rounded text-sm transition ${
                  size === sizeOption.value
                    ? 'bg-primary text-white'
                    : 'bg-muted text-text hover:bg-muted/80'
                }`}
              >
                {sizeOption.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-blue-900/20 border border-blue-700/30 rounded p-3">
          <p className="text-xs text-gray-400">
            After creating the widget, you can add technical indicators using the dropdown on the widget card.
          </p>
        </div>
      </form>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="flex-1 bg-muted text-text px-4 py-2 rounded hover:bg-muted/80 transition"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="flex-1 bg-primary text-white px-4 py-2 rounded hover:bg-primary/80 transition disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Widget'}
        </button>
      </div>
    </div>
  );

  // Step 3: Configuration for Options Chart (cascading dropdowns)
  const renderStepThreeOptionsChart = () => (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Ticker Selection */}
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Underlying Ticker
          </label>
          <SearchableTicker
            onChange={handleTickerChange}
            value={selectedTicker}
            placeholder="Search ticker (e.g., AAPL, SPY, NVDA)..."
          />
          {!isStatic && (
            <p className="text-xs text-gray-400 mt-2">
              Current default: <span className="text-primary font-medium">{lastClickedTicker}</span>
              {' '}(Updates when you click contracts in Screener/Watchlist)
            </p>
          )}
        </div>

        {/* Option Type Selection */}
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Option Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleOptionTypeChange({ target: { value: 'call' } })}
              className={`px-4 py-3 rounded text-sm font-medium transition ${
                optionType === 'call'
                  ? 'bg-green-700 text-white'
                  : 'bg-muted text-text hover:bg-muted/80'
              }`}
            >
              📈 Call
            </button>
            <button
              type="button"
              onClick={() => handleOptionTypeChange({ target: { value: 'put' } })}
              className={`px-4 py-3 rounded text-sm font-medium transition ${
                optionType === 'put'
                  ? 'bg-red-600 text-white'
                  : 'bg-muted text-text hover:bg-muted/80'
              }`}
            >
              📉 Put
            </button>
          </div>
        </div>

        {/* Expiration Date Selection */}
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Expiration Date
          </label>
          <ExpirationDateSelect
            ticker={selectedTicker?.value}
            value={expiration}
            onChange={handleExpirationChange}
          />
          {!selectedTicker?.value && (
            <p className="text-xs text-gray-500 mt-1">Select a ticker first</p>
          )}
        </div>

        {/* Strike Price Selection */}
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Strike Price
          </label>
          <StrikePriceSelect
            ticker={selectedTicker?.value}
            optionType={optionType}
            expiration={expiration}
            value={strike}
            onChange={setStrike}
          />
          {(!selectedTicker?.value || !expiration) && (
            <p className="text-xs text-gray-500 mt-1">
              {!selectedTicker?.value ? 'Select a ticker first' : 'Select an expiration date first'}
            </p>
          )}
        </div>

        {/* Chart History Range */}
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Chart History
          </label>
          <div className="grid grid-cols-4 gap-2">
            {['1m', '3m', '6m', '1y'].map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setDateRange(range)}
                className={`px-3 py-2 rounded text-sm transition ${
                  dateRange === range
                    ? 'bg-primary text-white'
                    : 'bg-muted text-text hover:bg-muted/80'
                }`}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Widget Size */}
        <div>
          <label className="block text-text text-sm font-medium mb-2">
            Widget Size
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
            ].map((sizeOption) => (
              <button
                key={sizeOption.value}
                type="button"
                onClick={() => setSize(sizeOption.value)}
                className={`px-3 py-2 rounded text-sm transition ${
                  size === sizeOption.value
                    ? 'bg-primary text-white'
                    : 'bg-muted text-text hover:bg-muted/80'
                }`}
              >
                {sizeOption.label}
              </button>
            ))}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-green-900/20 border border-green-700/30 rounded p-3">
          <p className="text-xs text-gray-400">
            This chart shows historical daily closing prices for the selected options contract.
            {!isStatic && ' Dynamic widgets will update when you click contracts in Screener or Watchlist.'}
          </p>
        </div>
      </form>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={handleBack}
          className="flex-1 bg-muted text-text px-4 py-2 rounded hover:bg-muted/80 transition"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !selectedTicker?.value || !expiration || !strike}
          className="flex-1 bg-primary text-white px-4 py-2 rounded hover:bg-primary/80 transition disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Widget'}
        </button>
      </div>
    </div>
  );

  // Step 3: Route to appropriate form based on widget type
  const renderStepThree = () => {
    if (widgetType === 'options_chart') {
      return renderStepThreeOptionsChart();
    }
    return renderStepThreePriceChart();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-background border border-muted rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-muted">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-primary">Add Widget</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-text transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2">
            <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
            <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
            <div className={`flex-1 h-1 rounded ${step >= 3 ? 'bg-primary' : 'bg-muted'}`} />
          </div>

          <div className="flex justify-between text-xs text-gray-400 mt-2">
            <span className={step === 1 ? 'text-primary font-medium' : ''}>Type</span>
            <span className={step === 2 ? 'text-primary font-medium' : ''}>Behavior</span>
            <span className={step === 3 ? 'text-primary font-medium' : ''}>Config</span>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {step === 1 && renderStepOne()}
          {step === 2 && renderStepTwo()}
          {step === 3 && renderStepThree()}
        </div>
      </div>
    </div>
  );
}

export default AddWidgetModal;
